// ═══════════════════════════════════════════════════════════════════
// XYQAR — API Client (typed + Auth0 token injection)
// ═══════════════════════════════════════════════════════════════════

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(`API ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function getToken(): Promise<string | null> {
  // Auth0 token from localStorage (set by Auth0Provider callback)
  const stored = localStorage.getItem('xyqar_auth0_token');
  if (stored) return stored;
  // Fallback mock
  const mock = localStorage.getItem('xyqar_mock_user');
  if (mock) return 'mock-token';
  return null;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(res.status, err);
  }

  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ─── Typed API calls ─────────────────────────────────────────────

// Client portal
export const clientApi = {
  dashboard: () => api.get<any>('/client/dashboard'),
  missions: () => api.get<any>('/client/missions'),
  experts: () => api.get<any>('/client/experts'),
  cra: () => api.get<any>('/client/cra'),
  invoices: () => api.get<any>('/client/invoices'),
  reports: () => api.get<any>('/client/reports'),
  approveProfile: (wolfId: string, action: string) =>
    api.post<any>(`/client/profiles/${wolfId}/approve`, { action }),
  validateCra: (craId: string, action: string, comment?: string) =>
    api.post<any>(`/client/cra/${craId}/validate`, { action, comment }),
  missionAction: (missionId: string, action: string) =>
    api.post<any>(`/client/missions/${missionId}/action`, { action }),
  startOnboarding: (data: any) => api.post<any>('/client/onboarding', data),
};

// Expert portal
export const expertApi = {
  dashboard: () => api.get<any>('/expert/dashboard'),
  missions: () => api.get<any>('/expert/missions'),
  profile: () => api.get<any>('/expert/profile'),
  updateProfile: (data: any) => api.put<any>('/expert/profile', data),
  cra: () => api.get<any>('/expert/cra'),
  submitCra: (data: any) => api.post<any>('/expert/cra', data),
  payments: () => api.get<any>('/expert/payments'),
};

// Exec portal
export const execApi = {
  dashboard: () => api.get<any>('/exec/dashboard'),
  devis: () => api.get<any>('/exec/devis'),
  createDevis: (data: any) => api.post<any>('/exec/devis', data),
  contracts: () => api.get<any>('/exec/contracts'),
  createContract: (devisId: string) => api.post<any>('/exec/contracts', { devis_id: devisId }),
  invoices: () => api.get<any>('/exec/invoices'),
  createInvoice: (data: any) => api.post<any>('/exec/invoices', data),
  markPaid: (invoiceId: string) => api.post<any>(`/exec/invoices/${invoiceId}/pay`),
  accounting: () => api.get<any>('/exec/accounting'),
  snapshots: () => api.get<any>('/exec/snapshots'),
  signatures: () => api.get<any>('/exec/signatures'),
  requestSignature: (data: any) => api.post<any>('/exec/signatures/request', data),
  completeSignature: (sigId: string, otp: string) =>
    api.post<any>(`/exec/signatures/${sigId}/complete`, { otp_code: otp }),
};

// Admin portal
export const adminApi = {
  dashboard: () => api.get<any>('/admin/dashboard'),
  opportunities: (status?: string) =>
    api.get<any>(`/admin/opportunities${status ? `?status=${status}` : ''}`),
  experts: () => api.get<any>('/admin/experts'),
  outreachPackages: () => api.get<any>('/admin/outreach/packages'),
  approveOutreach: (packageId: string, action: string) =>
    api.post<any>(`/admin/outreach/${packageId}/approve`, { action }),
  pipeline: () => api.get<any>('/admin/pipeline'),
  tenants: () => api.get<any>('/admin/tenants'),
  upsertTenant: (data: any) => api.post<any>('/admin/tenants', data),
  rgpdExecute: (data: any) => api.post<any>('/admin/rgpd/execute', data),
  matchingRecommend: () => api.post<any>('/admin/matching/recommend'),
};

// Referral / Affiliation
export const referralApi = {
  create: (data: any) => api.post<any>('/referral/create', data),
  mine: () => api.get<any>('/referral/mine'),
  stats: () => api.get<any>('/referral/stats'),
  all: (limit = 100) => api.get<any>(`/referral/all?limit=${limit}`),
  transition: (id: string, data: any) => api.post<any>(`/referral/${id}/transition`, data),
  payouts: (id: string) => api.get<any>(`/referral/${id}/payouts`),
  calculateRecurring: (periodStart: string, periodEnd: string) =>
    api.post<any>('/referral/recurring/calculate', { period_start: periodStart, period_end: periodEnd }),
  updateTiers: () => api.post<any>('/referral/tiers/update'),
  expire: () => api.post<any>('/referral/expire'),
  resolve: (code: string) => api.get<any>(`/referral/resolve/${code}`),
};

// Blind Profile
export const blindApi = {
  profiles: (limit = 50) => api.get<any>(`/blind/profiles?limit=${limit}`),
  profile: (wolfId: string, stage?: string) =>
    api.get<any>(`/blind/profile/${wolfId}${stage ? `?stage=${stage}` : ''}`),
  anonymize: (data: any) => api.post<any>('/blind/anonymize', data),
  decision: (wolfId: string, action: string, reason?: string) =>
    api.post<any>('/blind/decision', { wolf_id: wolfId, action, reason }),
  chatFilter: (wolfId: string, message: string) =>
    api.post<any>('/blind/chat/filter', { wolf_id: wolfId, message }),
  biasDetect: (clientId: string) => api.get<any>(`/blind/bias/${clientId}`),
  fairness: (clientId: string) => api.get<any>(`/blind/fairness/${clientId}`),
  revealRules: () => api.get<any>('/blind/reveal-rules'),
  audit: (wolfId: string) => api.get<any>(`/blind/audit/${wolfId}`),
};
